import dayjs, { Dayjs } from "dayjs";
import { DayOfTheWeek } from "../requests/dtos/Hours";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const dayJsTimeFormat = "HH:mm:ss";
export const dayJsTimeFormatDisplay = "hh:mm A";

export enum BusinessTimeZone {
  Eastern = "Eastern",
  Central = "Central",
  Mountain = "Mountain",
  Pacific = "Pacific",
}

export type BusinessHours = {
  useBusinessHours: boolean;
  timeZone: BusinessTimeZone;
  shutOffAccessWhenClosed: boolean;
  useLoadoutHoursForReceiving: boolean;
  loadoutHours: HoursSettings;
  receivingHours: HoursSettings;
}

export type HoursSettings = {
  [key in DayOfTheWeek]: BusinessHoursInterval[];
}

export type BusinessHoursInterval = {
  openTime: string | null;
  closeTime: string | null;
}

export type TimeSpan = {
  openTime: Dayjs | null;
  closeTime: Dayjs | null;
}

export type ValidationError = {
  [day in DayOfTheWeek]?: {
    openTimeError?: string;
    closeTimeError?: string;
  }[];
}

const overnightThreshold = dayjs("06:00:00", dayJsTimeFormat);

export const isOvernightHours = (openTime: Dayjs | null, closeTime: Dayjs | null) =>
  openTime?.isAfter(closeTime) && !closeTime?.isAfter(overnightThreshold);

export const getTimeSpans = (hours: HoursSettings, day: DayOfTheWeek): TimeSpan[] =>
  hours?.[day]?.map((interval) => ({
    openTime: interval.openTime ? dayjs(interval.openTime, dayJsTimeFormat) : null,
    closeTime: interval.closeTime ? dayjs(interval.closeTime, dayJsTimeFormat) : null,
  })) ?? [];

const hasOverlap = (
  currentOpenTime: Dayjs | null, currentCloseTime: Dayjs | null, currentDay: DayOfTheWeek,
  nextOpenTime: Dayjs | null, nextDay: DayOfTheWeek
) => {
  if (currentOpenTime === null || currentCloseTime === null || nextOpenTime === null)
    return false;

  if (currentDay === nextDay)
    return isOvernightHours(currentOpenTime, currentCloseTime) || currentCloseTime.isAfter(nextOpenTime);

  if (getNextDay(currentDay) === nextDay && isOvernightHours(currentOpenTime, currentCloseTime))
    return currentCloseTime.isAfter(nextOpenTime);
  
  return false;
}

const validateTimeSpan = (openTime: Dayjs | null, closeTime: Dayjs | null) => {
  let openTimeError = "";
  let closeTimeError = "";

  if (!openTime && !closeTime) return { openTimeError, closeTimeError };
  
  if (!openTime || !openTime.isValid()) openTimeError = "Invalid open time";
  if (!closeTime || !closeTime.isValid()) closeTimeError = "Invalid close time";

  if (openTime && closeTime) {
    if (!openTime.isBefore(closeTime) && !isOvernightHours(openTime, closeTime)) {
      openTimeError = "Open time must be before close time except for overnight hours";
      closeTimeError = "Close time must be after open time except for overnight hours";
    }
  }

  return { openTimeError, closeTimeError };
};

const getNextDay = (day: DayOfTheWeek) => {
  const days = Object.values(DayOfTheWeek);
  const dayOfWeekIndex = days.indexOf(day);

  const nextDayOfWeekIndex = (dayOfWeekIndex + 1) < 7
    ? dayOfWeekIndex + 1
    : 0;

  return days[nextDayOfWeekIndex];
}

export const validateHours = (hours: HoursSettings) => {
  const days = Object.values(DayOfTheWeek);

  var errors = Object.fromEntries(
    Object.keys(hours).map((day) => [day, new Array(hours[day as DayOfTheWeek].length).fill({ openTimeError: "", closeTimeError: "" })])
  );

  const allSpans = Object.entries(hours).flatMap(([day, intervals]) =>
    intervals.map((interval, index) => ({
      key: `${day}-${index}`,
      day: day as DayOfTheWeek,
      dayIndex: days.indexOf(day as DayOfTheWeek),
      openTime: interval.openTime ? dayjs(interval.openTime, dayJsTimeFormat) : null,
      closeTime: interval.closeTime ? dayjs(interval.closeTime, dayJsTimeFormat) : null,
      originalIndex: index,
    }))
  );

  const sortedSpans = allSpans.sort((a, b) => {
    if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
    if (!a.openTime || !b.openTime) return 0;
    return a.openTime.valueOf() - b.openTime.valueOf();
  });

  for (let i = 0; i < sortedSpans.length; i++) {
    const current = sortedSpans[i];
    const { openTimeError, closeTimeError } = validateTimeSpan(current.openTime, current.closeTime);

    if (openTimeError || closeTimeError) {
      errors[current.day]![current.originalIndex] = { openTimeError, closeTimeError };
      continue;
    }

    for (let j = 0; j < sortedSpans.length; j++) {
      if (i === j) continue;

      const next = sortedSpans[j];

      const isSatToSunCompare = current.day === DayOfTheWeek.Saturday && next.day === DayOfTheWeek.Sunday;
      const isRegularCompare = j > i && (current.day === next.day || getNextDay(current.day) === next.day);

      if (!isSatToSunCompare && !isRegularCompare) continue;

      const nextErrors = validateTimeSpan(next.openTime, next.closeTime);
      if (nextErrors.openTimeError || nextErrors.closeTimeError) continue;

      if (hasOverlap(current.openTime, current.closeTime, current.day, next.openTime, next.day)) {
        const overlapping = { openTimeError: "Overlapping hours", closeTimeError: "Overlapping hours" };
        errors[current.day][current.originalIndex] = overlapping;
        errors[next.day][next.originalIndex] = overlapping;
      }
    }
  }

  return errors;
};